import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EventCard, ServiceKind } from '@/api/types';
import { Button } from '@/components/kit/Button';
import { ActionBar } from '@/components/kit/ActionBar';
import { Alert, EmptyState, Note } from '@/components/kit/Note';
import { SectionHeader } from '@/components/kit/ObjectTabs';
import { Panel } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { CardLabel, MobileSub, Na, Table } from '@/components/kit/Table';
import { date, num, timeRange } from '@/lib/format';
import { SEATING, SELECTION, SERVICE_KIND } from '../state';
import { ServiceModal } from './ServiceModal';

/**
 * Вкладка «Услуги». Три типа, у каждого своя таблица: колонки у них
 * разные настолько, что одна общая таблица была бы наполовину пустой.
 *
 * Мерча здесь нет: услуга — доступ к чему-то на мероприятии.
 * У мерча остатки, размеры и цвета, это отдельный модуль (БЛ §3.4).
 */
export function ServicesTab({ event }: { event: EventCard }) {
  const navigate = useNavigate();
  const [kind, setKind] = useState<ServiceKind>('masterclass');
  const [adding, setAdding] = useState(false);

  const list = event.services.filter((s) => s.kind === kind);
  const inPackages = (id: string) => event.packages.filter((p) => p.serviceIds.includes(id)).length;
  const orphanTickets = event.services.filter((s) =>
    event.computed.ticketsWithoutBlock.includes(s.id),
  );

  const open = (id: string) => navigate(`/events/${event.id}/services/${id}`);

  const addButton = (
    <Button small tone="quiet" onClick={() => setAdding(true)}>
      ＋ Услуга
    </Button>
  );

  return (
    <>
      <Panel>
        {/* Мастер-классы, билеты и шоукейсы — разные предметы с разными
            колонками, поэтому подразделы, а не фильтр (кит 2.11) */}
        <SectionHeader
          segments={{
            value: kind,
            onChange: setKind,
            items: (['masterclass', 'ticket', 'showcase'] as ServiceKind[]).map((k) => ({
              value: k,
              label: SERVICE_KIND[k].plural,
            })),
          }}
          actions={addButton}
        />

        {kind === 'ticket' && orphanTickets.length ? (
          <Alert>
            <b>Билетов без блока: {orphanTickets.length}.</b>
            <span>
              {orphanTickets.map((t) => t.name).join(', ')} — блок удалили, билет никуда не пускает
            </span>
          </Alert>
        ) : null}

        {!list.length ? (
          <EmptyState>{SERVICE_KIND[kind].plural} ещё не заведены</EmptyState>
        ) : kind === 'masterclass' ? (
          <Table
            valueWidth={120}
            head={
              <tr>
                <th className="srt">
                  Класс <span className="ar">▲</span>
                </th>
                <th className="r">Время</th>
                <th className="r">Мест</th>
                <th className="r">В пакетах</th>
                <th />
              </tr>
            }
          >
            {list.map((s) => (
              <tr key={s.id} className="clk" onClick={() => open(s.id)}>
                <td data-col="primary">
                  <span className="nm">{s.name}</span>
                  <MobileSub>
                    {timeRange(s.startsAt, s.endsAt) ?? 'время не задано'}
                    {s.capacity ? ` · ${num(s.capacity)} мест` : ''}
                  </MobileSub>
                </td>
                <td className="r num" data-col="secondary">
                  {timeRange(s.startsAt, s.endsAt) ?? <span className="dim">время не задано</span>}
                </td>
                <td className="r num muted" data-col="hide">
                  {s.capacity ? num(s.capacity) : <Na />}
                </td>
                <td className="r" data-col="sec2">
                  <CardLabel>в пакетах</CardLabel>
                  <span className="num">{inPackages(s.id)}</span>
                </td>
                <td className="r" data-col="act" />
              </tr>
            ))}
            <tr className="tot">
              <td colSpan={2}>Мест всего</td>
              <td className="r num">{num(list.reduce((a, s) => a + (s.capacity ?? 0), 0))}</td>
              <td colSpan={2} />
            </tr>
          </Table>
        ) : kind === 'ticket' ? (
          <>
            <Table
              valueWidth={120}
              head={
                <tr>
                  <th>Билет</th>
                  <th>Блок</th>
                  <th>Дата</th>
                  <th>Рассадка</th>
                  <th className="r">Мест</th>
                  <th className="r">В пакетах</th>
                  <th />
                </tr>
              }
            >
              {list.map((s) => {
                const block = event.blocks.find((b) => b.id === s.blockId);
                return (
                  <tr key={s.id} className="clk" onClick={() => open(s.id)}>
                    <td data-col="primary">
                      <span className="nm">{s.name}</span>
                      <MobileSub>
                        {block?.name ?? 'блок удалён'}
                        {block ? ` · ${date(block.date)}` : ''}
                        {s.capacity ? ` · ${num(s.capacity)} мест` : ''}
                      </MobileSub>
                    </td>
                    <td className="muted" data-col="hide">
                      {block ? block.name : <span className="wtx">блок удалён</span>}
                    </td>
                    <td className="muted num" data-col="hide">
                      {block ? date(block.date) : <Na />}
                    </td>
                    <td className="muted" data-col="hide">
                      {s.seating ? SEATING[s.seating] : <Na />}
                    </td>
                    <td className="r" data-col="secondary">
                      <CardLabel>мест</CardLabel>
                      <span className="num">{s.capacity ? num(s.capacity) : <Na />}</span>
                    </td>
                    <td className="r" data-col="sec2">
                      <CardLabel>в пакетах</CardLabel>
                      <span className="num">
                        {inPackages(s.id) || <span className="na">—</span>}
                      </span>
                    </td>
                    <td className="r" data-col="act" />
                  </tr>
                );
              })}
            </Table>
            <Note>
              <b>Билет всегда на один блок.</b> Вместимость зала — свойство блока, а не товара.
              Доступ на несколько блоков продаётся пакетом из нескольких билетов
            </Note>
          </>
        ) : (
          <>
            <Table
              head={
                <tr>
                  <th>Шоукейс</th>
                  <th>Хореограф</th>
                  <th>Отбор</th>
                  <th className="r">В пакетах</th>
                  <th />
                </tr>
              }
            >
              {list.map((s) => (
                <tr key={s.id} className="clk" onClick={() => open(s.id)}>
                  <td data-col="primary">
                    <span className="nm">{s.name}</span>
                    <MobileSub>{s.choreographer ?? '—'}</MobileSub>
                  </td>
                  <td className="muted ell" data-col="hide">
                    {s.choreographer ?? <Na />}
                  </td>
                  <td data-col="secondary">
                    <Pill tone={s.selection === 'video' ? 'warn' : 'mut'}>
                      {s.selection ? SELECTION[s.selection] : 'без отбора'}
                    </Pill>
                  </td>
                  <td className="r" data-col="sec2">
                    <CardLabel>в пакетах</CardLabel>
                    <span className="num">{inPackages(s.id) || <span className="na">—</span>}</span>
                  </td>
                  <td className="r" data-col="act" />
                </tr>
              ))}
            </Table>
            <Note>
              Постановочный шоукейс оплачивают те, кто не участвует в чемпионате. Участникам команд
              он входит в пакет — они уже оплатили участие
            </Note>
          </>
        )}
      </Panel>

      <ActionBar>{addButton}</ActionBar>

      {adding ? (
        <ServiceModal event={event} initialKind={kind} onClose={() => setAdding(false)} />
      ) : null}
    </>
  );
}
